/**
 * Chat assistant — the slot vocabulary. EVERYTHING the model may emit is an
 * enum defined here (the only free text is `summary`, capped and filtered
 * through `containsProhibitedClaim` at coercion — see the case below),
 * so the Zod schema (`model-schema.ts`) is closed and nothing authored in
 * Sanity or typed by a user can widen it. See ai-chat-spec.md §3.2.
 *
 * Pure module: no I/O, no React, safe in Vitest.
 */
import { DIRECTORY_CATEGORIES, insightsCategoryToServiceCategories, type DirectoryCategory } from "@/lib/categories";
import { LANGUAGE_CODES } from "@/lib/lang-utils";
import { LOCATION_SLUGS, type LocationSlug } from "@/lib/provinces";
import { REQUEST_TIMELINE_OPTIONS } from "@/lib/requests";
import { containsProhibitedClaim } from "@/lib/copy-compliance";

// ── Intents → directory categories ─────────────────────────────────────────
export const INTENTS = [
  "company_formation",
  "work_permit_visa",
  "property",
  "tax_accounting",
  "insurance",
  "relocation",
  "translation_notary",
  // 2026-09-20 (owner): Permit and HR joined the homepage pills, footer and
  // chat choices once each had listings.
  "permits_licences",
  "hr_payroll",
  "legal_help",
  "other",
] as const;
export type Intent = (typeof INTENTS)[number];

/** Which directory categories a recommendation for this intent queries.
 *  `Record<Intent, …>` so the compiler enumerates it; values are checked
 *  against DIRECTORY_CATEGORIES in slots.test.ts. `other` → no match query.
 *
 *  `legal_help` is the generic "I need a lawyer" opener — one of the most
 *  natural first messages, which used to classify as `other` and dead-end at
 *  `gap` even though Legal is a directory category with listed professionals.
 *  It was kept off the chip row until 2026-09-18, when the owner asked for the
 *  row to cover every category the homepage hero offers (see chips.ts). The
 *  prompt still tells the model to prefer a specific intent when the user says
 *  what the lawyer is FOR. It routes to `match` when the user seeks a person
 *  (model-set branch). */
export const INTENT_TO_CATEGORIES: Record<Intent, readonly DirectoryCategory[]> = {
  company_formation:  ["Business", "Legal"],
  work_permit_visa:   ["Visa", "Legal"],
  property:           ["Property", "Legal"],
  tax_accounting:     ["Finance", "Audit"],
  insurance:          ["Insurance"],
  relocation:         ["Relocation", "Visa"],
  translation_notary: ["Translation", "Notary"],
  // Non-immigration permits; law firms obtain them too.
  permits_licences:   ["Permit", "Legal"],
  // Accountants run payroll too, so Finance widens a thin HR shelf.
  hr_payroll:         ["HR", "Finance"],
  legal_help:         ["Legal"],
  other:              [],
};

/**
 * The intents a CITED document's category makes RELEVANT — for the
 * "Find <X> professionals" offers under an answer (2026-08-24).
 *
 * Returns a SHORT ordered list, not one winner: an overstay article is `Visa`,
 * and a reader who just learned the fine wants a visa specialist OR a lawyer.
 * Both are real; offering only the first is arbitrary, and offering all ten is
 * the taxonomy menu this feature exists to replace (owner, 2026-08-24).
 *
 * Two steps, both driven by INTENT_TO_CATEGORIES so the relevance is the same
 * data the recommender itself queries on:
 *   1. SEED — the intent whose PRIMARY (first) category is the cited one.
 *      Guides store a DirectoryCategory directly; articles store an EDITORIAL
 *      category ("Legal & Visa") bridged exactly as `toFaqMenu` does, which can
 *      seed more than one.
 *   2. EXPAND — the seed intent's OTHER categories, mapped back through the
 *      same primary lookup. Visa → work_permit_visa, whose categories are
 *      ["Visa","Legal"], so Legal → legal_help joins it.
 *
 * A secondary-only category (Audit, Notary) is nobody's primary and simply
 * contributes nothing, so `tax_accounting` stays a single offer rather than
 * gaining a bogus partner. `other` never appears. Capped, because past three
 * this is a menu again.
 */
export const MAX_RELEVANT_INTENTS = 3;

const PRIMARY_CATEGORY_TO_INTENT: Partial<Record<DirectoryCategory, Intent>> = (() => {
  const m: Partial<Record<DirectoryCategory, Intent>> = {};
  for (const i of INTENTS) {
    const primary = INTENT_TO_CATEGORIES[i][0];
    // First-wins; the eight primaries are distinct today, so no collision.
    if (primary && !(primary in m)) m[primary] = i;
  }
  return m;
})();

/** The DirectoryCategories a cited document's category value stands for. */
function resolveCategories(category: string | null | undefined): readonly DirectoryCategory[] {
  if (!category) return [];
  return DIRECTORY_CATEGORIES.includes(category as DirectoryCategory)
    ? [category as DirectoryCategory]
    : insightsCategoryToServiceCategories(category);
}

export function intentsForCategory(category: string | null | undefined): Intent[] {
  const out: Intent[] = [];
  const push = (i: Intent | undefined) => {
    if (i && i !== "other" && !out.includes(i)) out.push(i);
  };
  const cats = resolveCategories(category);
  for (const c of cats) push(PRIMARY_CATEGORY_TO_INTENT[c]);
  // Expand from the seed only — expanding from every seed would let a
  // two-category article pull in four intents and become a menu again.
  if (out.length > 0) {
    for (const c of INTENT_TO_CATEGORIES[out[0]]) push(PRIMARY_CATEGORY_TO_INTENT[c]);
  }
  return out.slice(0, MAX_RELEVANT_INTENTS);
}

/**
 * The intents worth offering when we ALREADY KNOW the reader's intent — the
 * counterpart to `intentsForCategory`, seeded from the slot instead of from a
 * cited document's category.
 *
 * ⛔ It exists because the document's shelf was overriding the reader (fixed
 * 2026-09-07). Asked "how long does a work permit take in Hanoi?", the model
 * captured `work_permit_visa` + `ha-noi` CORRECTLY, and the offers still read
 * "Find company formation professionals" and "Find tax & accounting
 * professionals" — because they were built from the cited article, which is
 * shelved "Business & Tax" (its sibling work-permit article is under
 * "Legal & Visa", so the shelving is inconsistent as well). Re-shelving that one
 * article would not have fixed the class: ANY article whose category differs
 * from the reader's stated need reproduces it.
 *
 * Same expansion rule as `intentsForCategory`, so the two paths agree:
 * `work_permit_visa` → ["Visa","Legal"] → itself plus `legal_help`.
 */
export function intentsForIntent(intent: Intent): Intent[] {
  const out: Intent[] = [];
  const push = (i: Intent | undefined) => {
    if (i && i !== "other" && !out.includes(i)) out.push(i);
  };
  push(intent);
  if (out.length === 0) return out; // `other` tells us nothing
  for (const c of INTENT_TO_CATEGORIES[intent]) push(PRIMARY_CATEGORY_TO_INTENT[c]);
  return out.slice(0, MAX_RELEVANT_INTENTS);
}

/** The single most relevant intent, or null. Kept for callers that only need
 *  to know WHETHER anything is offer-able before paying for a counts read. */
export function intentForCategory(category: string | null | undefined): Intent | null {
  return intentsForCategory(category)[0] ?? null;
}

// ── Simple enums ───────────────────────────────────────────────────────────
export const BRANCHES = ["match", "checklist"] as const;
export type Branch = (typeof BRANCHES)[number];

/** VND bands. Edges are PLACEHOLDERS — calibrate against real price_display
 *  data before freezing (spec §16.2). v1 captures only; nothing filters on it. */
export const BUDGET_BANDS = ["lt_5m", "5_20m", "20_50m", "gt_50m", "unsure"] as const;
export type BudgetBand = (typeof BUDGET_BANDS)[number];

/**
 * The CEILING each band implies, in VND — null when the band sets none.
 *
 * We ask for a budget, repeat it back in the summary the user confirms, and
 * then proposed a professional whose cheapest package is four times it (owner,
 * 2026-08-24). Stating a constraint and then violating it is worse than never
 * asking, so the recommender now treats this as a hard filter.
 *
 * `gt_50m` is open-ended upwards and `unsure` is the user declining to say —
 * neither can exclude anyone, so both are null.
 */
export const BUDGET_CEILING_VND: Record<BudgetBand, number | null> = {
  lt_5m: 5_000_000,
  "5_20m": 20_000_000,
  "20_50m": 50_000_000,
  gt_50m: null,
  unsure: null,
};

/**
 * Is a professional affordable at this band?
 *
 * `minPriceVnd` is their cheapest PAID package (see lowestPaidPrice), or null.
 * NULL MEANS UNKNOWN, NEVER "too expensive": a professional who keeps prices
 * off their public listing (`show_prices`, which is enforced in the database)
 * would otherwise be silently excluded from every budgeted search — punished
 * for using a feature we sell. Absence of a price is not evidence of one, so
 * they stay in and the reader decides.
 */
export function withinBudget(minPriceVnd: number | null | undefined, band: string | undefined): boolean {
  if (!band) return true;
  const ceiling = BUDGET_CEILING_VND[band as BudgetBand];
  if (ceiling === null || ceiling === undefined) return true;
  if (minPriceVnd === null || minPriceVnd === undefined) return true;
  return minPriceVnd <= ceiling;
}

/** `language_pref` = a pro language code the user wants, or `any`. Defaults
 *  from the chat language when that is a code pros can list; otherwise asked. */
export const LANGUAGE_PREF_ANY = "any" as const;
export const LANGUAGE_PREF_VALUES: readonly string[] = [...LANGUAGE_CODES, LANGUAGE_PREF_ANY];

export const TIMELINE_VALUES = REQUEST_TIMELINE_OPTIONS;

/**
 * How the reader RESPONDED to the question we asked, as the model infers it
 * from the whole conversation (owner, 2026-09-25: "can we make the chat infer
 * context instead" of word lists). The model reports; code decides:
 *   answer         gave a usable value (the slot carries it)
 *   no_preference  answered, but has no preference / does not know / will not
 *                  or cannot say ("dont know", "idc", "broke", "tùy", "随便")
 *   question       asked something instead of answering
 *   about_results  asked about the professionals already shown ("do they all
 *                  do licencing?", "is any of them in Da Nang?")
 * Measured in production the day it was built: "dont know" and "idc" left the
 * timeline empty and the same question was asked three times; "broke" left the
 * budget empty; "do they all do licencing?" re-ran the search.
 */
//   request        asks US to find, recommend or connect them with a
//                  professional ("手伝ってくれる人を探しています", "find me an
//                  accountant"): never a web search (added 2026-09-25, when a
//                  Japanese request bought one and the reply advertised an
//                  outside firm; the web gate's own test is an EN/VI list).
export const RESPONSE_KINDS = ["answer", "no_preference", "question", "about_results", "request", "overview", "documents", "my_checklists"] as const;
export type ResponseKind = (typeof RESPONSE_KINDS)[number];

/**
 * The value a slot takes when the reader has no preference. Each is an
 * existing, already-valid value, so nothing new is persisted (a new slot or
 * flag key would fail `session-schema.ts` on the next turn). Slots absent here
 * (intent, branch, the checklist disambiguators) have no neutral answer and
 * keep being asked.
 */
export const NO_PREFERENCE_VALUE: Partial<Record<SlotKey, unknown>> = {
  city: "any",
  language_pref: ["any"],
  timeline: "Just exploring",
  budget: "unsure",
};
export type Timeline = (typeof REQUEST_TIMELINE_OPTIONS)[number];

/**
 * ⛔ `any` IS A CITY VALUE. Without it the match flow has a DEAD END, and it
 * is not a rare one: a user who is not in Vietnam yet, or who simply does not
 * mind, has no valid answer to "which city?". Live 2026-08-30 the owner typed
 * "any city is fine, im not in vietnam yet" TWICE and got "Which city or
 * province?" both times — the vocabulary could not express the answer, so the
 * turn made no progress, and three of those escalate to a throttle. The user
 * is punished for our missing enum value.
 *
 * `language_pref` has had `any` since it was written; `city` was the outlier.
 * It is NOT the same as `remote`, which means "no physical office needed" —
 * a real filter on the professional. `any` is the absence of a filter.
 */
export const ANY_CITY = "any";
export type CityValue = LocationSlug | typeof ANY_CITY;
export const CITY_VALUES: readonly CityValue[] = [...LOCATION_SLUGS, ANY_CITY];

/**
 * The province slug to FILTER on, or undefined for "do not filter".
 *
 * One helper because three call sites had already hand-written
 * `city !== "remote"` and adding a second exempt value to each by hand is how
 * they drift apart — recommend.ts would filter on `any` while the reply said
 * "professionals anywhere", or the /request prefill would read "in any".
 */
/**
 * The language codes to FILTER on, or undefined for "do not filter".
 *
 * Sibling of `cityFilter`, and for the same reason: `!== "any"` was about to be
 * hand-written at each consumer, and a set-valued slot makes that worse — one
 * site would filter on the array while another rendered "any" as a language
 * name. Filtering is ANY-of (owner, 2026-08-30): a professional who speaks one
 * of the listed languages can talk to this user, which is what listing them
 * means.
 */
/**
 * The most languages a user may choose at once.
 *
 * ONE constant, because it is enforced in three places that must agree — the
 * rendered row (`LANGUAGE_CHIPS_MAX`), the wire schema (`TurnRequestSchema`)
 * and the persisted schema (`SlotsSchema`). Raising the row alone would make a
 * full-row selection fail validation, i.e. `chip_invalid` on buttons WE DREW,
 * which is the one thing the chip contract promises never happens. Same
 * reasoning as `CHECKLIST_CHIP_LABELS_MAX` (review 2026-08-30).
 */
export const LANGUAGE_PREF_MAX = 6;

export function languageFilter(pref: readonly string[] | undefined): string[] | undefined {
  if (!pref?.length) return undefined;
  const codes = pref.filter((c) => c !== "any");
  return codes.length ? codes : undefined;
}

export function cityFilter(city: string | undefined): string | undefined {
  return city && city !== "remote" && city !== ANY_CITY ? city : undefined;
}

/** Conversation-language tags the model may return. A CLOSED list (never a
 *  pattern): this value reaches the shared checklist-translation prompt via a
 *  code→name map, so it must not be free text. ISO 639-1 subset + `yue` +
 *  `zh-Hant`.
 *
 *  ⛔ `zh` is Simplified (or undifferentiated) Chinese and must NEVER be
 *  renamed or removed: stored `chat_sessions` rows carry it, and a value this
 *  list no longer holds fails `parseSessionRow` on the next turn, which
 *  `validate_reset`s the session and wipes the reader's slots. `zh-Hant`
 *  (Traditional) was ADDED beside it (i18n-expansion-spec.md 9.1). */
export const LANG_ENUM = [
  "en","vi","fr","ja","ko","zh","zh-Hant","yue","de","ru","es","it","pt","th","id","ms",
  "ar","bn","cs","da","el","fi","he","hi","hu","km","lo","my","nl","no","pl",
  "ro","sv","tl","tr","uk","ur",
] as const;
export type Lang = (typeof LANG_ENUM)[number];

/** English names for the translation prompt — the ONLY way a language reaches
 *  that prompt (never the raw tag). */
export const LANG_NAMES: Record<Lang, string> = {
  en:"English", vi:"Vietnamese", fr:"French", ja:"Japanese", ko:"Korean", zh:"Chinese",
  "zh-Hant":"Traditional Chinese", yue:"Cantonese", de:"German", ru:"Russian", es:"Spanish", it:"Italian", pt:"Portuguese",
  th:"Thai", id:"Indonesian", ms:"Malay", ar:"Arabic", bn:"Bengali", cs:"Czech", da:"Danish",
  el:"Greek", fi:"Finnish", he:"Hebrew", hi:"Hindi", hu:"Hungarian", km:"Khmer", lo:"Lao",
  my:"Burmese", nl:"Dutch", no:"Norwegian", pl:"Polish", ro:"Romanian", sv:"Swedish",
  tl:"Filipino", tr:"Turkish", uk:"Ukrainian", ur:"Urdu",
};

/**
 * Each language's ENDONYM -- its name in itself.
 *
 * Deliberately not `Intl.DisplayNames`, for two reasons. A picker is how you
 * find YOUR language, and you look for the word you would write, not for
 * whatever the page's current locale calls it -- a Vietnamese reader on an
 * English page scans for "Tieng Viet", not "Vietnamese". And ICU data differs
 * between the Node build and the browser, so a server-rendered
 * `Intl.DisplayNames` list is a hydration mismatch waiting for the one locale
 * where they disagree (the React #418 family in CLAUDE.md). A literal map is
 * the same string in both places, always.
 *
 * `LANG_NAMES` stays separate and stays English: it feeds the translation
 * PROMPT, where the model needs the English name.
 */
export const LANG_ENDONYMS: Record<Lang, string> = {
  en:"English", vi:"Tiếng Việt", fr:"Français", ja:"日本語", ko:"한국어", zh:"中文",
  "zh-Hant":"繁體中文", yue:"粵語", de:"Deutsch", ru:"Русский", es:"Español", it:"Italiano", pt:"Português",
  th:"ไทย", id:"Bahasa Indonesia", ms:"Bahasa Melayu", ar:"العربية", bn:"বাংলা",
  cs:"Čeština", da:"Dansk", el:"Ελληνικά", fi:"Suomi", he:"עברית", hi:"हिन्दी",
  hu:"Magyar", km:"ភាសាខ្មែរ", lo:"ລາວ", my:"မြန်မာ", nl:"Nederlands", no:"Norsk",
  pl:"Polski", ro:"Română", sv:"Svenska", tl:"Filipino", tr:"Türkçe",
  uk:"Українська", ur:"اردو",
};

/**
 * The SPOKEN language a conversation language stands for, as a professional's
 * `languages` code (lang-utils `LANGUAGE_CODES`). The two scripts of Chinese
 * are one spoken language: a reader writing Traditional wants a professional
 * who speaks `zh` exactly as a Simplified reader does. Everything else is its
 * own code already.
 */
export function spokenLanguageOf(lang: string): string {
  return lang === "zh-Hant" ? "zh" : lang;
}

/**
 * The profile-translation locale (`PROFILE_TRANSLATION_LOCALES`) a
 * conversation language reads cards in. The chat's `zh` is Simplified, which
 * the profile tables call `zh-Hans`; `zh-Hant` is the same tag in both. Every
 * other language passes through, so `cardLocaleFor` decides exactly as before.
 */
export function profileLangOf(lang: string): string {
  return lang === "zh" ? "zh-Hans" : lang;
}

/** Is this an allowed conversation/translation language tag? The one place a
 *  client-supplied language is admitted (the checklist card's picker), so it
 *  must be a membership test against the closed list and never a shape test. */
export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANG_ENUM as readonly string[]).includes(v);
}

// ── Checklist disambiguator slots (CODE-defined; Sanity may only reference) ─
export const CHECKLIST_SLOTS = {
  // `local_100` added 2026-08-26 (owner): the enum was foreigner-framed and a
  // Vietnamese user opening a fully domestic company had no honest answer —
  // the audience rule (don't narrow to expats) applies to vocabularies too.
  // No checklist covers it yet, so its tap lands on the honest gap +
  // hand-off; the gap marker is the demand signal for authoring one.
  ownership:   ["foreign_100", "local_100", "joint_venture", "local_partner"],
  sector:      ["software", "consulting", "trading", "manufacturing", "fnb", "other"],
  // Extended 2026-08-26 from the published "Types of Companies" article
  // (owner). llc / jsc / branch / rep_office keep their slugs — the Sanity
  // company checklists MATCH on entity_type="llc", so renaming or splitting
  // llc (one- vs multi-member is a LABEL distinction, not a value) would
  // invalidate those docs at catalogue load. The three additions have no
  // checklist yet: their pills exist because the widened ask shows the full
  // enum, and a tap lands on the honest gap + hand-off.
  // "Individual commercial activity" and "agricultural households" from the
  // same article are deliberately absent: they are not company formations.
  entity_type: ["llc", "jsc", "partnership", "sole_prop", "household", "branch", "rep_office"],
  // Renting and buying a home are two different procedures wearing one
  // intent (owner call 2026-08-23). The property checklist used to carry
  // both, 5 "Lease:" items beside 7 "Purchase:" ones, and every reader was
  // shown the third of the list that was not theirs — the prefixes on every
  // label were the workaround. This is the disambiguator that lets the
  // catalogue answer the question instead.
  tenure:      ["rent", "buy"],
  // A work permit and a family member's TT visa are different procedures
  // under one intent, the `tenure` situation again (2026-08-27). It is also
  // what makes a dependant checklist SAFE to publish: two match-less
  // checklists under one intent do not tie, `selectChecklist` sorts them on
  // `id.localeCompare` and silently delivers the first, so a dependant doc
  // would have hijacked every work-permit question with nothing reporting a
  // fault. Values are CODE-owned (like tenure, unlike ownership/sector/
  // entity_type, which Sanity content defines), so their chip labels live in
  // `messages` and beat the model's.
  // "How can I get a business visa?" delivered the WORK PERMIT checklist
  // (live, 2026-08-30): work_permit_visa is one intent, wp-2026 was its only
  // checklist with an empty match, and "business visa" was INEXPRESSIBLE in
  // this vocabulary — so the nearest bucket delivered, confidently, by
  // construction. A business visa (DN) and a work permit are different legal
  // documents; conflating them is the dangerous class for this product.
  // `procedure` is the axis those questions live on. Values are CODE-owned
  // (labels in `messages` + `canned.ts`, like tenure/applicant). Only
  // work_permit has a checklist today — every other value lands on the honest
  // gap + reading card + hand-off, and each gap logged is the demand signal
  // for authoring that checklist. NOTE: the drafted dependant checklist
  // should match procedure=dependant_visa; `applicant` is superseded before
  // any published doc ever used it (kept: stored sessions may carry it).
  procedure:   ["work_permit", "business_visa", "tourist_visa", "investor_visa", "dependant_visa"],
  applicant:   ["self", "dependant"],
} as const;
export type ChecklistSlotKey = keyof typeof CHECKLIST_SLOTS;
/** Ask order when several disambiguators are pending. */
export const CHECKLIST_SLOT_KEYS = Object.keys(CHECKLIST_SLOTS) as ChecklistSlotKey[];
export function isChecklistSlotKey(k: string): k is ChecklistSlotKey {
  return k in CHECKLIST_SLOTS;
}

/**
 * The values a disambiguator ASK presents — the slot's FULL closed enum,
 * catalogue-covered values first (owner, 2026-08-25: a lone "100% foreign-
 * owned" pill hid the other ownership shapes entirely). `selectChecklist`
 * still decides FLOW from the covered values alone; presentation widens so a
 * joint-venture reader can answer truthfully and land on the honest `gap`
 * outcome (with its professional hand-off) instead of being shown a menu that
 * pretends their case does not exist. Covered-first keeps the deliverable
 * paths at the top, in catalogue priority order.
 */
export function checklistAskValues(slot: SlotKey, covered: readonly string[]): string[] {
  if (!isChecklistSlotKey(slot)) return [...covered];
  const all = CHECKLIST_SLOTS[slot] as readonly string[];
  return [...covered.filter((v) => all.includes(v)), ...all.filter((v) => !covered.includes(v))];
}

// ── The slot record ────────────────────────────────────────────────────────
// BUMPED to 2 on 2026-08-30: `language_pref` became an array, which is a
// persisted SHAPE change, and this constant's own doc says to bump when that
// happens. Not cosmetic — `parseSessionRow` distinguishes "written by a NEWER
// client" (schema_version_reset) from "malformed" (validate_reset), and after
// a rollback every post-deploy row would otherwise be diagnosed as tampering.
// `migrateSlots` upconverts v1 rows, INCLUDING those with no language_pref:
// the schema pins `v` with z.literal, so a v1 row that is not re-stamped fails
// outright and wipes a live session.
export const SLOTS_VERSION = 2 as const;

export interface Slots {
  v: typeof SLOTS_VERSION;
  intent?: Intent;
  branch?: Branch;
  city?: CityValue;
  /**
   * Languages the professional should speak — a SET, not one value (owner,
   * 2026-08-30). A person who works in Japanese and English wants either, and
   * before this could name only one. Filtering is ANY-of: a pro speaking one
   * of them can talk to the user, which is what listing them means.
   *
   * `["any"]` is the explicit no-preference answer and never mixes with codes.
   * Legacy single-string rows are migrated in `migrateSlots` — see the
   * E-RESET scar: a slot that persists in a shape the reader rejects destroys
   * the session one turn later.
   */
  language_pref?: string[]; // each ∈ LANGUAGE_PREF_VALUES
  timeline?: Timeline;
  budget?: BudgetBand;
  /** Budget is asked at most once; set by the reducer when it asks. */
  budget_asked?: boolean;
  summary?: string;
  summary_confirmed?: boolean;
  /** `intent=other` gets exactly one clarifying turn. */
  other_clarified?: boolean;
  ownership?: (typeof CHECKLIST_SLOTS.ownership)[number];
  sector?: (typeof CHECKLIST_SLOTS.sector)[number];
  entity_type?: (typeof CHECKLIST_SLOTS.entity_type)[number];
  tenure?: (typeof CHECKLIST_SLOTS.tenure)[number];
  procedure?: (typeof CHECKLIST_SLOTS.procedure)[number];
  applicant?: (typeof CHECKLIST_SLOTS.applicant)[number];
  /** Needs the message carried BESIDES `intent` (multi-need, 2026-08-27).
   *  Validated, deduped and capped by `normaliseAlsoNeeds`; never the
   *  primary, never `other`. Offered as follow-up chips, never served. */
  also_needs?: Intent[];
  delivered_checklist_id?: string;
}
export type SlotKey = Exclude<keyof Slots, "v">;

/**
 * Slots a question can be ABOUT — the closed vocabulary for the model's `asks`
 * field, which declares which slot its reply is asking for so the route can
 * verify that against the reducer's own answer (spec §4.3: model proposes,
 * code disposes). Excludes bookkeeping flags the user is never asked about
 * (`budget_asked`, `other_clarified`, `summary`, `delivered_checklist_id`).
 */
export const ASKABLE_SLOT_KEYS: readonly SlotKey[] = [
  "intent", "branch", "city", "language_pref", "timeline", "budget",
  "summary_confirmed", ...CHECKLIST_SLOT_KEYS,
];

/**
 * Match branch — required, IN ASK ORDER.
 *
 * ⚠ THIS ORDER WAS NEVER EXERCISED UNTIL 2026-08-30. `language_pref` sat at
 * index 2 while `computeMissing` skipped it for every chat language a
 * professional can list — which is en/vi/ja/zh/ko/fr, i.e. essentially every
 * real user. So the effective order was intent → city → timeline, and the
 * position of `language_pref` was decoration. The moment the ask was turned on
 * (owner's request, same day) that decoration became load-bearing and the very
 * first follow-up to "I need a lawyer in Ho Chi Minh City" was "what language
 * would you prefer?" — which the owner rightly flagged.
 *
 * ⛔ `timeline` IS NOT ASKED (owner, 2026-10-01: "remove the timeline
 * question"). It filters nothing: `runRecommendEffect` queries on category,
 * place, language and budget, and the effect key carries no timeline, so the
 * answer changed no card on any screen. Measured over 30 days: 113 turns spent
 * on it, asked third, i.e. 113 turns of delay before the first card. It stays
 * in the vocabulary (old rows carry it, `effectiveBranch` reads it as funnel
 * evidence) and a reader may still VOLUNTEER it ("within a month",
 * `VOLUNTEERABLE_SLOTS`), which reaches the team through the request
 * hand-off (`composeRequestDescription`), the one place it is read.
 *
 * Language stays inside the REQUIRED set (before the optional budget ask) so
 * it is still answered before any professional is shown.
 */
export const REQUIRED_MATCH: readonly SlotKey[] = ["intent", "city", "language_pref"];
/** Slots the model may set on ANY typed turn (a user may volunteer or correct
 *  these at any time). Everything else is accepted only when it is `missing[0]`. */
export const ALWAYS_OPEN_SLOTS: ReadonlySet<SlotKey> = new Set<SlotKey>(["intent", "city", "branch"]);

/**
 * Slots the MODEL may fill from prose on a typed turn even when they are not
 * `missing[0]` — i.e. what a user is allowed to VOLUNTEER while answering
 * something else.
 *
 * Deliberately WIDER than `ALWAYS_OPEN_SLOTS`, and deliberately a separate
 * constant: that set also gates CHIP taps, where the narrow rule is a real
 * guard ("a chip for a slot that is not being asked is refused" — a stale or
 * forged client must not be able to set an arbitrary slot). Widening the two
 * together broke that adversarial fixture; only the prose path should widen.
 *
 * Why widen at all (E2E run 2026-08-21): "…in Ho Chi Minh City, I speak
 * English, within a month" had its timeline extracted CORRECTLY and then
 * thrown away because `missing[0]` was still `intent`, so the assistant asked
 * "When do you need this by?" one turn later — in EN and VI alike. It reads as
 * not listening, and costs a turn.
 *
 * Safe because every added slot is enum-validated by `coerceSlotValue`, so
 * accepting one early cannot skip a state — `computeMissing` still requires
 * the whole REQUIRED_MATCH set, it just stops re-asking what was already said.
 * Still gated: `summary` (free text; only when it IS the asked slot),
 * `summary_confirmed` (chip-only), and the checklist disambiguators
 * (`selectChecklist` decides which one is live).
 */
export const VOLUNTEERABLE_SLOTS: ReadonlySet<SlotKey> = new Set<SlotKey>([
  ...ALWAYS_OPEN_SLOTS, "language_pref", "timeline", "budget",
]);
/** Slots that can only ever be set by a chip (never by the model). */
export const CHIP_ONLY_SLOTS: ReadonlySet<SlotKey> = new Set<SlotKey>(["summary_confirmed"]);

/** Most checklist chip labels that may be persisted on `flags` for the next
 *  turn's transcript lookup. SHARED by the write (turn.ts) and the loader's Zod
 *  cap (session-schema.ts) — they must never drift: a write the loader rejects
 *  makes the NEXT turn `validate_reset`, which ENDS the session and wipes every
 *  slot the user filled. 8 leaves headroom over the largest CHECKLIST_SLOTS
 *  entry (`sector`, 6). */
export const CHECKLIST_CHIP_LABELS_MAX = 8;

export const SUMMARY_MAX = 500;
export const REPLY_MAX = 400;
export const CHIP_LABEL_MAX = 40;
export const USER_TEXT_MAX = 1000;
/** What the COMPOSER lets a user type (owner, 2026-08-27). Deliberately a
 *  separate, smaller constant: `USER_TEXT_MAX` also bounds STORED transcript
 *  entries (session-schema) and assistant text, so shrinking it would
 *  validate-reset every live session — and the server keeps accepting up to
 *  `USER_TEXT_MAX` as the backstop so a pre-limit draft or a retried older
 *  message is never rejected. */
export const USER_INPUT_MAX = 500;
/** Show the character count only from here — a counter nobody is near is
 *  noise (owner: no helper text until it matters). */
export const USER_INPUT_WARN = 460;
export const TRANSCRIPT_MAX = 30;

/** Runtime membership test for a slot value. Returns the canonical value or null. */
export function coerceSlotValue(slot: SlotKey, raw: unknown): unknown | null {
  if (raw === undefined || raw === null) return null;
  switch (slot) {
    case "intent":            return (INTENTS as readonly string[]).includes(String(raw)) ? raw : null;
    case "branch":            return (BRANCHES as readonly string[]).includes(String(raw)) ? raw : null;
    case "city":              return (CITY_VALUES as readonly string[]).includes(String(raw)) ? raw : null;
    case "language_pref": {
      // Accepts a bare string too: the MODEL still emits one sometimes, and a
      // user typing "Japanese" is not wrong. Normalised to an array here so
      // every consumer sees one shape (the `also_needs` precedent).
      const list = Array.isArray(raw) ? raw : [raw];
      const v = list
        .filter((x): x is string => typeof x === "string")
        .filter((x) => LANGUAGE_PREF_VALUES.includes(x));
      const uniq = [...new Set(v)];
      if (uniq.length === 0) return null;
      // "any" is an ANSWER, not a member: mixing it with codes is incoherent
      // ("English, Vietnamese, or it doesn't matter"), and the codes are the
      // more specific statement, so they win.
      // SORTED, and that is load-bearing twice over. `effectKey` interpolates
      // this array, so ["en","ja"] and ["ja","en"] produced DIFFERENT keys for
      // the same answer: a model re-emitting the set in another order made the
      // effect look stale, re-ran the query, reshuffled the cards, and on the
      // next load the hydrate refused and the cards vanished from the
      // transcript. It also stops an echoed value reading as a change (see the
      // value-compare in reducer.ts) — a new array is never `===` the old one.
      const codes = uniq.filter((x) => x !== "any").sort();
      return codes.length ? codes : ["any"];
    }
    case "timeline":          return (TIMELINE_VALUES as readonly string[]).includes(String(raw)) ? raw : null;
    case "budget":            return (BUDGET_BANDS as readonly string[]).includes(String(raw)) ? raw : null;
    // `summary` is the ONLY model-written free text that survives the turn, and
    // it does not stay inside the chat: `buildRequestPrefill` puts it in the
    // /request form's description (stored in `requests`, read by an admin) and
    // `writeContactPrefill` puts it in the message to a professional. Both are
    // Easytiger-authored text for compliance purposes, so HARD RULE 6 applies
    // — and it was NOT enforced here, while two comments claimed it was
    // (audit 2026-08-22). Rejecting leaves the slot unset, which is the same
    // handling as any other value that does not fit: the model is asked again.
    case "summary":           return typeof raw === "string" && raw.trim() && raw.length <= SUMMARY_MAX && !containsProhibitedClaim(raw) ? raw.trim() : null;
    case "summary_confirmed": return raw === true || raw === "true" ? true : null;
    case "budget_asked":
    // The first ARRAY slot. Validated element-wise against the closed
    // vocabulary; an all-invalid array coerces to null so it is DROPPED
    // rather than stored empty, keeping "the model said nothing" and "the
    // model said only nonsense" the same thing downstream.
    case "also_needs": {
      if (!Array.isArray(raw)) return null;
      const v = raw.filter((x): x is string => typeof x === "string" && (INTENTS as readonly string[]).includes(x) && x !== "other");
      return v.length ? [...new Set(v)] : null;
    }
    case "other_clarified":   return raw === true || raw === "true" ? true : null;
    case "delivered_checklist_id": return typeof raw === "string" && /^[A-Za-z0-9-]+$/.test(raw) && !raw.startsWith("drafts.") ? raw : null;
    // EVERY checklist slot, listed once. `tenure` was added to the vocabulary
    // and missed here (2026-08-23): the catalogue asked the question, the
    // chips rendered, and every tap came back E-CHIP-INVALID because the
    // value fell through to `default: null`. tsc cannot catch it — the case
    // list is not exhaustive over CHECKLIST_SLOT_KEYS — so the test iterates
    // the vocabulary instead. If you add a slot, it goes here too.
    case "ownership":
    case "sector":
    case "entity_type":
    case "tenure":
    case "applicant":
    case "procedure":         return (CHECKLIST_SLOTS[slot] as readonly string[]).includes(String(raw)) ? raw : null;
    default:                  return null;
  }
}

/** Every category the taxonomy knows — re-exported for tests that assert the
 *  intent map only names real categories. */
export const KNOWN_CATEGORIES: readonly string[] = DIRECTORY_CATEGORIES;
